import React, { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import { formatApiError } from "../../lib/api";
import { adminPost } from "../../lib/adminApi";
import { clearAppStorage } from "../../lib/appStorage";
import { invalidateAllApiCache } from "../../lib/apiCache";
import { Button, FormTextField } from "../ui";
import {
  WorkflowModalBody,
  WorkflowModalFooter,
  WorkflowModalHeader,
  WorkflowModalShell,
} from "../workflow/WorkflowModal";

export default function SwitchUserModal({ open, onClose }) {
  const dialogRef = useRef(null);
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const nav = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) setEmail("");
  }, [open]);

  useEffect(() => {
    if (!open || busy) return undefined;
    const frame = requestAnimationFrame(() => {
      document.getElementById(`${baseId}-email`)?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [open, busy, baseId]);

  if (!open) return null;

  async function handleSubmit(e) {
    e?.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      const data = await adminPost("/switch-user", { email: trimmed });
      clearAppStorage();
      await invalidateAllApiCache();
      signIn({ token: data.token, account: data.organizer });
      toast.success(`Switched to ${data.organizer.name || trimmed}`);
      onClose?.();
      nav("/events", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <WorkflowModalShell
      dialogRef={dialogRef}
      onClose={busy ? undefined : onClose}
      onBackdropClose={busy ? undefined : onClose}
      testId="switch-user-modal"
      titleId={titleId}
      busy={busy}
      dismissOnEscape={!busy}
    >
      <WorkflowModalHeader
        title="Switch user"
        titleId={titleId}
        subtitle="Sign in as another account (admin only)."
        onClose={busy ? undefined : onClose}
        closeTestId="switch-user-close"
      />
      <form onSubmit={handleSubmit}>
        <WorkflowModalBody>
          <FormTextField
            id={`${baseId}-email`}
            label="Account email"
            type="email"
            autoComplete="off"
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            placeholder="user@example.com"
            disabled={busy}
            data-testid="switch-user-email"
          />
        </WorkflowModalBody>
        <WorkflowModalFooter>
          <div className="flex flex-wrap gap-2 justify-end">
            <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={busy || !email.trim()}
              data-testid="switch-user-submit"
            >
              {busy ? "Switching…" : "Switch"}
            </Button>
          </div>
        </WorkflowModalFooter>
      </form>
    </WorkflowModalShell>
  );
}
